import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import AuthService from '../../services/AuthService';
import BuilderService from '../../services/BuilderService';

const card = {
  background: '#fff',
  borderRadius: 16,
  boxShadow: '0 4px 24px rgba(0,0,0,.07)',
};

/** List builders — Create opens /addbuilder; Edit opens /editbuilder/:id (matches Property inventory pattern). */
export default function Builders() {
  const toast = useRef(null);
  const navigate = useNavigate();
  const clientId = AuthService.getLoggedInClientId();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const list = await BuilderService.getByClientId(clientId);
      setRows(Array.isArray(list) ? list : []);
    } catch {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Could not load builders' });
      setRows([]);
    }
    setLoading(false);
  }, [clientId]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((b) => {
      const hay = [b.companyName, b.legalName, b.city, b.state, b.email, b.phone]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [rows, search]);

  const remove = async (row) => {
    if (
      !window.confirm(
        `Deactivate "${row.companyName || 'this builder'}"? Linked channel properties keep their snapshot; new listings should pick an active builder.`,
      )
    )
      return;
    try {
      await BuilderService.delete(row.id);
      toast.current?.show({ severity: 'success', summary: 'Done', detail: 'Builder deactivated', life: 2000 });
      await load();
    } catch {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Could not deactivate' });
    }
  };

  if (!clientId) {
    return (
      <div className="container-fluid py-4">
        <p className="text-muted mb-0">Sign in with a tenant account to manage builders.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: 24, background: '#f4f7fa', minHeight: '100vh' }}>
      <Toast ref={toast} />

      {/* Header — same structure as Property Inventory */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#1a1f36' }}>
            <i className="fas fa-hard-hat" style={{ color: '#5e72e4', marginRight: 10 }} />
            Builders &amp; partners
          </h3>
          <p style={{ margin: '4px 0 0', color: '#8898aa' }}>
            Channel developers — default commission &amp; contacts for partner inventory.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/addbuilder')}
          style={{
            padding: '12px 24px',
            background: 'linear-gradient(135deg,#5e72e4,#825ee4)',
            color: '#fff',
            border: 'none',
            borderRadius: 12,
            cursor: 'pointer',
            fontWeight: 700,
            fontSize: 14,
          }}
        >
          <i className="fas fa-plus" style={{ marginRight: 8 }} />
          Add Builder
        </button>
      </div>

      {/* Filters row */}
      <div style={{ ...card, padding: '14px 18px', marginBottom: 20, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
        <input
          placeholder="Search company, city, email, phone…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: '1 1 220px', padding: '8px 14px', borderRadius: 9, border: '1px solid #e3e6ec', fontSize: 13 }}
        />
      </div>

      <p style={{ color: '#8898aa', fontSize: 13, marginBottom: 14 }}>
        Showing <strong style={{ color: '#1a1f36' }}>{filtered.length}</strong> of {rows.length} builders
      </p>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: 32, color: '#5e72e4' }} />
          <p style={{ color: '#8898aa', marginTop: 12 }}>Loading builders…</p>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ ...card, padding: '60px', textAlign: 'center' }}>
          <i className="fas fa-hard-hat" style={{ fontSize: 48, color: '#e3e6ec', marginBottom: 16 }} />
          <h5 style={{ color: '#8898aa', marginBottom: 8 }}>{rows.length === 0 ? 'No builders yet' : 'No matches'}</h5>
          <p className="text-muted small mb-3 mx-auto" style={{ maxWidth: 420 }}>
            Add external developers you co-market with. Link them when creating <strong>channel partner</strong> property inventory.
          </p>
          {rows.length === 0 && (
            <button
              type="button"
              onClick={() => navigate('/addbuilder')}
              style={{
                padding: '10px 24px',
                background: '#5e72e4',
                color: '#fff',
                border: 'none',
                borderRadius: 10,
                cursor: 'pointer',
                fontWeight: 700,
              }}
            >
              Add your first builder
            </button>
          )}
        </div>
      ) : (
        <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table className="table table-hover align-middle mb-0" style={{ fontSize: '14px' }}>
              <thead>
                <tr style={{ background: 'linear-gradient(135deg,#5e72e4,#825ee4)' }}>
                  {['Company', 'Location', 'Default %', 'Contact', 'Actions'].map((h) => (
                    <th
                      key={h}
                      className={h === 'Actions' ? 'text-end pe-4' : 'ps-4'}
                      style={{
                        padding: '13px 16px',
                        color: '#fff',
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        border: 'none',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((b) => (
                  <tr key={b.id} style={{ borderBottom: '1px solid #f0f2f5' }}>
                    <td className="ps-4 py-3">
                      <div className="fw-semibold text-dark">{b.companyName || '—'}</div>
                      {b.legalName && <div className="small text-muted">{b.legalName}</div>}
                    </td>
                    <td className="py-3 text-secondary">{[b.city, b.state].filter(Boolean).join(', ') || '—'}</td>
                    <td className="py-3">{b.defaultCommissionPercent != null ? `${b.defaultCommissionPercent}%` : '—'}</td>
                    <td className="py-3">
                      <div>{b.phone || '—'}</div>
                      <div className="small text-muted">{b.email || ''}</div>
                    </td>
                    <td className="pe-4 py-3 text-end">
                      <button
                        type="button"
                        onClick={() => navigate(`/editbuilder/${b.id}`)}
                        className="btn btn-sm btn-outline-primary rounded-pill px-3 me-2"
                      >
                        <i className="fas fa-edit me-1" />
                        Edit
                      </button>
                      <button type="button" onClick={() => remove(b)} className="btn btn-sm btn-outline-danger rounded-pill px-3">
                        <i className="fas fa-ban me-1" />
                        Deactivate
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
